import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await api.get('/gallery');
        setGalleryItems(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        // Fallback to mock data if API fails
        setGalleryItems([
          {
            id: 1,
            title: 'Annual Day Celebration',
            category: 'Events',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-12-15'
          },
          {
            id: 2,
            title: 'Science Fair',
            category: 'Academic',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-11-20'
          },
          {
            id: 3,
            title: 'Sports Day',
            category: 'Sports',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-10-05'
          },
          {
            id: 4,
            title: 'Cultural Program',
            category: 'Events',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-09-15'
          },
          {
            id: 5,
            title: 'Math Olympiad Winners',
            category: 'Academic',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-08-25'
          },
          {
            id: 6,
            title: 'Basketball Tournament',
            category: 'Sports',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-07-30'
          },
          {
            id: 7,
            title: 'Campus View',
            category: 'Campus',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-06-10'
          },
          {
            id: 8,
            title: 'Laboratory Session',
            category: 'Academic',
            imageUrl: '/api/placeholder/300/200',
            date: '2023-05-15'
          }
        ]);
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  const categories = ['All', 'Events', 'Sports', 'Campus', 'Academic'];

  const filteredItems = activeCategory === 'All' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === activeCategory);

  return (
    <div className="min-h-screen py-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">Photo Gallery</h1>
        
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map(category => (
            <button
              key={category}
              className={`px-4 py-2 rounded-full ${
                activeCategory === category
                  ? 'bg-primary text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredItems.map(item => (
            <div 
              key={item.id} 
              className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-transform hover:scale-105"
              onClick={() => setSelectedImage(item)}
            >
              <div className="bg-gray-200 border-2 border-dashed rounded-t-lg w-full h-48 flex items-center justify-center">
                <img 
                  src={item.imageUrl} 
                  alt={item.title} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 truncate">{item.title}</h3>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-sm text-gray-600">{item.category}</span>
                  <span className="text-sm text-gray-500">{item.date}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Modal for image preview */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedImage(null)}
          >
            <div 
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-4 flex justify-between items-center border-b">
                <h2 className="text-xl font-semibold">{selectedImage.title}</h2>
                <button 
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                  onClick={() => setSelectedImage(null)}
                >
                  &times;
                </button>
              </div>
              <div className="p-4">
                <div className="bg-gray-200 border-2 border-dashed rounded-lg w-full h-96 flex items-center justify-center">
                  <img 
                    src={selectedImage.imageUrl} 
                    alt={selectedImage.title} 
                    className="max-h-[70vh] max-w-full object-contain"
                  />
                </div>
                <div className="mt-4 flex justify-between items-center">
                  <span className="text-gray-600">{selectedImage.category}</span>
                  <span className="text-gray-600">{selectedImage.date}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gallery Info */}
        <div className="mt-12 bg-white rounded-lg shadow-md p-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">About Our Gallery</h2>
          <p className="text-gray-600 mb-4">
            Explore our collection of memorable moments from school events, academic achievements, 
            sports competitions, and campus life. Our gallery showcases the vibrant and dynamic 
            atmosphere of our educational institution.
          </p>
          <p className="text-gray-600">
            From annual day celebrations to science fairs, from sports tournaments to cultural programs, 
            our gallery captures the essence of student life and achievements at our school.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Gallery;